import React from 'react';
import { motion } from 'framer-motion';
import { Globe, FileText, Video, Target } from 'lucide-react';
import { trackWhatsAppClick } from '../hooks/useMetaPixel';
const services = [{
  icon: Globe,
  title: 'Páginas Web',
  description: 'Sitios web profesionales que muestran tu negocio, generan confianza y convierten visitantes en clientes.',
  color: 'text-purple-400',
  bg: 'bg-purple-500/10',
  border: 'border-purple-500/20',
  hoverBorder: 'hover:border-purple-500/50',
  glow: 'group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]'
}, {
  icon: FileText,
  title: 'Landing Pages',
  description: 'Páginas de aterrizaje optimizadas para campañas específicas. Diseñadas para una sola acción: que te contacten.',
  color: 'text-cyan-400',
  bg: 'bg-cyan-500/10',
  border: 'border-cyan-500/20',
  hoverBorder: 'hover:border-cyan-500/50',
  glow: 'group-hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]'
}, {
  icon: Video,
  title: 'Marketing Digital',
  description: 'Guión, grabación, edición y copywriting. Contenido estratégico que atrae, engancha y convierte seguidores en clientes.',
  color: 'text-pink-400',
  bg: 'bg-pink-500/10',
  border: 'border-pink-500/20',
  hoverBorder: 'hover:border-pink-500/50',
  glow: 'group-hover:shadow-[0_0_30px_rgba(236,72,153,0.15)]',
  tags: ['Guión', 'Grabación', 'Edición', 'Copy']
}, {
  icon: Target,
  title: 'Meta Ads',
  description: 'Campañas publicitarias en Facebook e Instagram. Segmentación precisa para llevar clientes reales a tu negocio.',
  color: 'text-yellow-400',
  bg: 'bg-yellow-500/10',
  border: 'border-yellow-500/20',
  hoverBorder: 'hover:border-yellow-500/50',
  glow: 'group-hover:shadow-[0_0_30px_rgba(234,179,8,0.15)]'
}];
export function ServicesSection() {
  return <section id="servicios" className="py-24 bg-[#050510] relative overflow-hidden" data-id="element-370">
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-371" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-purple-900/10 rounded-full blur-[120px] pointer-events-none" data-id="element-372" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-373">
        <div className="text-center mb-16" data-id="element-374">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-sm font-medium mb-6" data-id="element-375">
            Lo que hacemos
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-5xl font-bold font-orbitron mb-4" data-id="element-376">
            Nuestros <span className="text-gradient-cosmic" data-id="element-377">Servicios</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-378">
            Todo lo que tu negocio necesita para crecer en el mundo digital. De
            la A a la Z.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-379">
          {services.map((service, index) => <motion.div key={service.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`group relative p-8 rounded-2xl bg-[#0a0a1a] border ${service.border} ${service.hoverBorder} transition-all duration-300 hover:-translate-y-2 ${service.glow} shadow-lg`} data-id="element-380">
              <div className={`w-14 h-14 rounded-xl ${service.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} data-id="element-381">
                <service.icon className={`w-7 h-7 ${service.color}`} data-id="element-382" />
              </div>

              <h3 className="text-xl font-bold font-orbitron text-white mb-3" data-id="element-383">
                {service.title}
              </h3>

              <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-384">
                {service.description}
              </p>

              {service.tags && <div className="flex flex-wrap gap-2" data-id="element-385">
                  {service.tags.map(tag => <span key={tag} className={`text-xs px-2.5 py-1 rounded-full ${service.bg} ${service.color} font-medium`} data-id="element-386">
                      {tag}
                    </span>)}
                </div>}
            </motion.div>)}
        </div>

        {/* Automation Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5
      }} className="mt-10" data-id="element-387">
          <a href="https://wa.me/50685316690?text=Hola%2C%20me%20interesa%20saber%20más%20sobre%20los%20sistemas%20de%20automatización%20para%20mi%20negocio." target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick()} className="group flex items-center justify-between gap-6 w-full p-6 md:p-8 rounded-2xl bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.15)]" data-id="element-388">
            <div className="flex items-center gap-4" data-id="element-389">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0" data-id="element-390">
                <span className="text-2xl" data-id="element-391">⚡</span>
              </div>
              <div data-id="element-392">
                <h3 className="text-lg md:text-xl font-bold font-orbitron text-white" data-id="element-393">
                  Sistemas que Automatizan tu Trabajo
                </h3>
                <p className="text-sm text-gray-400 mt-1" data-id="element-394">
                  Ahorrá tiempo y escalá tu negocio con automatizaciones
                  inteligentes.
                </p>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white font-bold text-sm border border-white/10 group-hover:bg-white/20 transition-colors flex-shrink-0" data-id="element-395">
              Consultanos →
            </span>
          </a>
        </motion.div>
      </div>
    </section>;
}